import { Theme, ThemeConsumer } from '@rneui/themed';
import React, { RefObject, useState } from 'react';
import {
	View, TouchableOpacity, StyleSheet, Platform,
	TextInput
} from 'react-native';
import { ZenIcon, ZenText } from 'zen-core-elements';
import { CustomSearchBar } from '../../customSearchBar';
import { isValidAmount } from '../../../../redux/utils/discountUtils';
import CommonService from '../../../../shared/services/commonService';
import { useAppSelector } from '../../../../redux/hooks';
import { paymentSaveType } from '../../../../redux/types/sellScreenTypes';

const CashPayment = ({ onClose, handleSave, initialValue = 0, inputRef }: { onClose: () => void, handleSave: (value: paymentSaveType) => void, initialValue?: number, inputRef: RefObject<TextInput | null> }) => {
	const [value, setValue] = useState<null | string>(initialValue ? initialValue?.toString() : null);
	const { totalAmount, isSplitPayment, remainingAmount } = useAppSelector((state) => state?.NewSellscreenPaymentSlice)
	const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'

	const newTotalAmount = isSplitPayment ? (remainingAmount + initialValue) : totalAmount

	const getSmartQuickPay = (total: number) => {
		if (total <= 0) return [];

		const suggestions = new Set<number>();
		const notes = [10, 20, 50, 100, 200, 500, 2000];

		// 1. Always offer the Exact Amount
		suggestions.add(total);

		// 2. Add the next multiple of 10 or 50 (if not already round)
		const next10 = Math.ceil(total / 10) * 10;
		const next50 = Math.ceil(total / 50) * 50;
		if (next10 > total) suggestions.add(next10);
		if (next50 > total) suggestions.add(next50);

		// 3. Find the next 3 higher standard notes
		// This handles the "130" case correctly: it looks for notes > 130 (200, 500, 2000)
		for (let note of notes) {
			if (note > total) {
				suggestions.add(note);
			}
			if (suggestions.size >= 5) break; // Limit processing
		}

		// 4. Special case for very large amounts (e.g., 1389)
		// Add next 100 or next 500 if not already present
		const next100 = Math.ceil(total / 100) * 100;
		const next500 = Math.ceil(total / 500) * 500;
		if (next100 > total) suggestions.add(next100);
		if (next500 > total) suggestions.add(next500);

		// Final Cleanup: Sort, remove duplicates, and pick the best 4
		return Array.from(suggestions)
			.filter(amt => amt >= total)
			.sort((a, b) => a - b)
			.slice(0, 4);
	};

	const quickPayOptions = getSmartQuickPay(newTotalAmount);


	const onchangeText = (text: string | null) => {
		if (!text || isValidAmount(text)) {
			setValue(text)
		}
	}

	const disabledSave = () => {
		if (isSplitPayment) {
			if (!value) {
				return true
			}
			return false
		} else {
			if (!value || Number(value) < newTotalAmount) {
				return true
			}
			return false
		}
	}

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<View>
						{isSplitPayment ? null : <View style={styles.header}>
							<ZenText content={'Cash Payment'} style={styles.headerTitle} />
							<TouchableOpacity onPress={onClose} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
								<ZenIcon name='close' type='antdesign' color={theme?.lightDark} size={24} />
							</TouchableOpacity>
						</View>}

						<View style={styles.content}>

							{isSplitPayment ?
								<View style={styles.headerCard}>
									<ZenText content={'Balance'} style={[styles.label, { marginBottom: 0, color: theme?.grey, fontFamily: theme?.bold }]} />
									<ZenText content={CommonService?.currencyStandard(currencySymbol, (remainingAmount - (Number(value) - initialValue)) > 0 ? (remainingAmount - (Number(value) - initialValue))?.toString() : "0")} style={styles.totalAmount} />
								</View>
								: <View style={styles.headerCard}>
									<ZenText content={'Total Amount'} style={[styles.label, { marginBottom: 0, color: theme?.grey, fontFamily: theme?.bold }]} />
									<ZenText content={CommonService?.currencyStandard(currencySymbol, newTotalAmount?.toString())} style={styles.totalAmount} />
								</View>}
							{/* Value Input */}
							<ZenText content='Cash Given' style={styles.label} />

							<View style={{ paddingBottom: 10 }}>
								<CustomSearchBar
									inputRef={inputRef}
									placeholder='Enter a amount'
									search={value}
									setSearch={onchangeText}
									toggleOffset={() => { }}
									backgroundColor={theme?.iceWhite}
									leftIcon={false}
									border={true}
									height={50}
									keyboardType='number-pad'
									leftComponent={<ZenText content={currencySymbol} style={styles.currencyPrefix} />}
								/>
							</View>

							{/* Quick Presets */}
							<View style={styles.presetRow}>
								{quickPayOptions.map((p) => (
									<TouchableOpacity
										key={p}
										style={styles.presetChip}
										onPress={() => setValue(p.toString())}
									>
										<ZenText content={`+${CommonService?.currencyStandard(currencySymbol, p?.toString())}`} style={[styles.presetText]} />
									</TouchableOpacity>
								))}
							</View>
						</View>

						{/* Footer Actions */}
						<View style={styles.footer}>
							<TouchableOpacity style={[styles.cancelBtn, isSplitPayment && { backgroundColor: theme?.lightBackgroundColor }]} onPress={onClose}>
								<ZenText content={isSplitPayment ? 'Back' : "Cancel"} style={styles.cancelBtnText} />
							</TouchableOpacity>
							<TouchableOpacity
								style={[styles.addBtn, disabledSave() && styles.disabledBtn]}
								disabled={disabledSave()}
								onPress={() => handleSave({
									paidAmount: Number(value),
									reason: "",
									amount: (remainingAmount - (Number(value) - initialValue)) > 0 ? Number(value) : totalAmount
								})}
							>
								<ZenText content={'Confirm Cash'} style={[styles.addBtnText, disabledSave() && { color: theme?.disableTextColor }]} />
							</TouchableOpacity>
						</View>
					</View>
				)
			}}
		</ThemeConsumer>
	);
};

const style = (theme: Theme) => StyleSheet.create({

	headerCard: {
		alignItems: 'center',
		marginBottom: 16
	},
	totalAmount: { fontSize: theme?.bigFontSize + 10, fontFamily: theme?.bold, color: theme?.primary },

	header: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		// paddingHorizontal: 24,
		paddingBottom: 16,
		borderBottomWidth: 1,
		borderBottomColor: theme?.softSlate,
	},
	headerTitle: {
		fontSize: theme?.mediumFontSize,
		fontFamily: theme?.bold,
		color: theme?.lightDark,
	},
	content: {
		paddingVertical: 24
	},
	label: {
		fontSize: theme?.errorFontSize,
		fontFamily: theme?.medium,
		color: theme?.disabledGray500,
		marginBottom: 8,
		textTransform: 'uppercase',
		letterSpacing: 0.5,
	},
	segmentContainer: {
		flexDirection: 'row',
		backgroundColor: theme.softSlate,
		borderRadius: 12,
		padding: 4,
		marginBottom: 24,
	},
	segment: {
		flex: 1,
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		paddingVertical: 10,
		borderRadius: 8,
		gap: 8,
	},
	activeSegment: {
		backgroundColor: theme?.light,
		...Platform.select({
			ios: { shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.05, shadowRadius: 4 },
			android: { elevation: 2 },
		}),
	},
	segmentText: {
		fontSize: theme?.smallHeaderFontSize,
		fontFamily: theme?.medium,
		color: theme?.disabledGray500,
	},
	activeSegmentText: {
		color: theme?.primary,
		fontFamily: theme?.bold,
	},
	inputContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		backgroundColor: theme?.iceWhite,
		borderWidth: 1.5,
		borderColor: theme?.coolGray,
		borderRadius: 16,
		paddingHorizontal: 16,
		height: 50,
	},
	currencyPrefix: {
		fontSize: theme?.mediumFontSize,
		fontFamily: theme?.medium,
		color: theme?.grey,
		marginRight: 5,
	},
	input: {
		flex: 1,
		fontSize: theme?.smallerFontSize,
		fontFamily: theme?.medium,
		color: theme?.lightDark,
	},
	presetRow: {
		flexDirection: 'row',
		gap: 10,
		marginTop: 16,
	},
	presetChip: {
		backgroundColor: theme?.tealPrimary,
		paddingVertical: 8,
		paddingHorizontal: 16,
		borderRadius: 10,
		borderWidth: 1,
		borderColor: theme?.lightPrimary,
	},
	presetText: {
		color: theme?.primary,
		fontSize: theme?.smallHeaderFontSize,
		fontFamily: theme?.medium,
	},
	footer: {
		flexDirection: 'row',
		paddingTop: 16,
		gap: 12,
		borderTopWidth: 1,
		borderTopColor: theme.softSlate,
	},
	cancelBtn: {
		flex: 1,
		paddingVertical: 14,
		alignItems: 'center',
		borderRadius: 12,
		backgroundColor: theme?.iceWhite,
	},
	cancelBtnText: {
		color: theme?.disabledGray500,
		fontSize: theme?.smallFontSize,
		fontFamily: theme?.medium,
	},
	addBtn: {
		flex: 2,
		backgroundColor: theme?.primary,
		paddingVertical: 14,
		alignItems: 'center',
		borderRadius: 12,
	},
	disabledBtn: {
		backgroundColor: theme?.disableGray,
	},
	addBtnText: {
		color: theme?.light,
		fontSize: theme?.smallFontSize,
		fontFamily: theme?.medium,
	}
});

export default CashPayment;